import React, { useEffect, useState } from "react";
import { CiUser } from "react-icons/ci";
import { IoIosArrowBack } from "react-icons/io";
import { FaCheck, FaTimes } from "react-icons/fa";
import ModuleHeader from "../../moduleHeader";
import { getCurrentPackageUserModule } from "../../../services/api";
import { getSynastryList, getSynastry, postSynastryRequest, updateSynastryRequest } from "../../../services/synastryService";
import Notiflix from "notiflix";

function Synastry({ id, moduleId, isOwner }) {
  const [currentStage, setCurrentStage] = useState(0); // 0: Liste, 1: Analiz
  const [synastryResult, setSynastryResult] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [showScanner, setShowScanner] = useState(false);
  const [synastryList, setSynastryList] = useState([]);
  const [packageData, setPackageData] = useState(null);
  const [pageLoading, setPageLoading] = useState(true);
  const [isHeaderExpanded, setIsHeaderExpanded] = useState(false);
  const [showNewSynastryPopup, setShowNewSynastryPopup] = useState(false);
  const [partnerEmail, setPartnerEmail] = useState("");
  const [isSending, setIsSending] = useState(false);
  const [updatingRequest, setUpdatingRequest] = useState(null);

  // Notiflix'i initialize et
  useEffect(() => {
    Notiflix.Notify.init({
      position: "top-right",
      timeout: 3000,
      clickToClose: true,
      pauseOnHover: true,
    });
  }, []);

  // Paket verisini çek
  useEffect(() => {
    if (!id) return;

    const fetchPackageData = async () => {
      try {
        const data = await getCurrentPackageUserModule(id, 6); // Synastry moduleId: 6
        setPackageData(data);
      } catch (error) {
        console.error("Paket verisi alınamadı:", error);
      } finally {
        setPageLoading(false);
      }
    };

    fetchPackageData();
  }, [id]);

  // Sinastri listesini çek
  useEffect(() => {
    const fetchList = async () => {
      try {
        setIsLoading(true);
        const response = await getSynastryList();
        // API yanıtı: { success: true, data: [...] }
        const items = Array.isArray(response?.data) ? response.data : [];
        setSynastryList(items);
      } catch (e) {
        console.error("Sinastri listesi yüklenemedi", e);
        Notiflix.Notify.failure("Sinastri listesi yüklenirken bir hata oluştu!");
        setSynastryList([]);
      } finally {
        setIsLoading(false);
      }
    };

    fetchList();
  }, []);

  // Yeni sinastri talebi gönder
  const handleSendSynastryRequest = async () => {
    if (!partnerEmail.trim()) {
      Notiflix.Notify.failure("Lütfen partner email adresini giriniz!");
      return;
    }

    try {
      setIsSending(true);
      const response = await postSynastryRequest({
        partner_email: partnerEmail.trim()
      });

      if (response.success) {
        Notiflix.Notify.success("Sinastri isteği başarıyla gönderildi!");
        setShowNewSynastryPopup(false);
        setPartnerEmail("");
        // Listeyi yenile
        const listRes = await getSynastryList();
        const items = Array.isArray(listRes?.data) ? listRes.data : [];
        setSynastryList(items);
      }
    } catch (error) {
      console.error("Sinastri isteği gönderilirken hata:", error);
      Notiflix.Notify.failure(
        error.response?.data?.message || "Sinastri isteği gönderilirken bir hata oluştu!"
      );
    } finally {
      setIsSending(false);
    }
  };

  // Sinastri isteğini güncelle (kabul et, reddet, sil)
  const handleUpdateRequest = async (uuid, action) => {
    try {
      setUpdatingRequest(uuid);
      const response = await updateSynastryRequest(uuid, action);

      if (response.success) {
        const actionText = action === 1 ? "kabul edildi" : action === 2 ? "reddedildi" : "silindi";
        Notiflix.Notify.success(`Sinastri isteği başarıyla ${actionText}!`);
        // Listeyi yenile
        const listRes = await getSynastryList();
        const items = Array.isArray(listRes?.data) ? listRes.data : [];
        setSynastryList(items);
      }
    } catch (error) {
      console.error("Sinastri isteği güncellenirken hata:", error);
      Notiflix.Notify.failure(
        error.response?.data?.message || "Sinastri isteği güncellenirken bir hata oluştu!"
      );
    } finally {
      setUpdatingRequest(null);
    }
  };

  const buildAnalysisCards = (result) => {
    if (!result) return [];
    const scores = result.scores || {};
    const aspects = result.summary?.key_aspects || {};

    const cardFor = (key, title, iconType, scoreValue, color, bgColor) => {
      const aspect = aspects[key] || {};
      let percent = typeof scoreValue === "number" ? Math.round(scoreValue) : 0;
      
      const subtitle =
        aspect?.best_aspect?.planets ||
        (aspect.status ? aspect.status.replace("_", " ") : "");
      const description = aspect?.message || "";
      return {
        id: key,
        title,
        percentage: percent,
        iconType,
        color,
        bgColor,
        subtitle,
        description,
      };
    };

    const cards = [
      cardFor("love", "Aşk", "heart", scores.love, "#EF4444", "#FEE2E2"),
      cardFor("communication", "İletişim", "chat", scores.communication, "#3B82F6", "#DBEAFE"),
      cardFor("passion", "Tutku", "fire", scores.passion, "#F59E0B", "#FEF3C7"),
    ];

    if (scores.friendship !== undefined || aspects.friendship)
      cards.push(cardFor("friendship", "Arkadaşlık", "users", scores.friendship, "#10B981", "#D1FAE5"));
    if (scores.challenge !== undefined || aspects.challenge)
      cards.push(cardFor("challenge", "Meydan Okuma", "lightning", scores.challenge, "#8B5CF6", "#EDE9FE"));

    return cards;
  };

  const handleOpenAnalysis = async (uuid) => {
    try {
      // Pending durumundaki synastry'leri açma
      const item = synastryList.find((i) => i.uuid === uuid);
      if (item?.request_status === "pending") {
        Notiflix.Notify.warning("Bu sinastri henüz kabul edilmedi!");
        return;
      }

      setIsLoading(true);
      const response = await getSynastry(uuid);
      // API yanıtı: { success: true, data: {...} }
      setSynastryResult(response?.data || null);
      setCurrentStage(1);
    } catch (e) {
      Notiflix.Notify.failure("Sinastri detayı yüklenemedi.");
      console.error(e);
    } finally {
      setIsLoading(false);
    }
  };

  // Loading screen
  if (pageLoading || !packageData) {
    return (
      <div className="w-full h-screen min-h-screen flex items-center justify-center bg-[#fff]">
        <div className="flex flex-col items-center justify-center">
          <div className="relative mb-8">
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="animate-spin rounded-full h-24 w-24 border-4 border-transparent border-t-[#3DA7B5] border-r-[#3DA7B5] opacity-75"></div>
            </div>
            <div className="absolute inset-0 flex items-center justify-center">
              <div
                className="animate-spin rounded-full h-16 w-16 border-[3px] border-transparent border-b-[#2a8a96] border-l-[#2a8a96] opacity-60"
                style={{
                  animationDirection: "reverse",
                  animationDuration: "1.5s",
                }}
              ></div>
            </div>
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="h-12 w-12 rounded-full bg-gradient-to-br from-[#3DA7B5] via-[#2a8a96] to-[#3DA7B5] animate-pulse shadow-2xl"></div>
            </div>
          </div>
          <p className="text-xl font-semibold text-gray-800 animate-pulse">
            Yükleniyor...
          </p>
        </div>
      </div>
    );
  }

  // Liste ekranı
  const renderListStage = () => {
    // Kullanıcı bilgileri
    const userData = packageData?.user
      ? {
          fullName: packageData.user.first_name + " " + packageData.user.last_name,
          profileImage: packageData.user.pictureDetails?.picture || "/assets/pngImages/user.png",
        }
      : {
          fullName: "Kullanıcı",
          profileImage: "/assets/pngImages/user.png",
        };

    // Tüm listeyi göster (pending olanlar da dahil)
    const filteredList = synastryList;

    return (
      <div className="w-full h-screen min-h-screen flex items-stretch justify-stretch sm:items-center sm:justify-center bg-[#e8e9ed] relative">
        <div className="flex flex-col items-center w-full min-h-full bg-white shadow-xl border border-gray-200 rounded-none sm:max-w-[390px] sm:h-[700px] sm:rounded-3xl relative overflow-y-auto">
          {/* ModuleHeader */}
          {packageData && (
            <div className="relative z-30 w-full mb-2">
              <ModuleHeader
                userName={
                  packageData.user?.first_name + " " + packageData.user?.last_name
                }
                subtitle={packageData.package?.name + " Modülüne Hoş Geldiniz"}
                title={packageData.package?.name}
                description={packageData.package?.description}
                backgroundImage={packageData.package?.image_url}
                onExpandedChange={setIsHeaderExpanded}
                showToggle={true}
                autoHeight={true}
              />
            </div>
          )}

          <div className="w-full px-4 py-6">
            <div className="max-w-sm mx-auto">
              {/* Yeni sinastri oluşturma butonu */}
              <button
                onClick={() => setShowNewSynastryPopup(true)}
                className="w-full group relative overflow-hidden bg-gradient-to-r from-[#3DA7B5] to-[#5ABCC8] hover:from-[#2F8894] hover:to-[#4A9BA6] text-white rounded-2xl mb-6 p-6 shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-[1.02] active:scale-[0.98]"
              >
                <div className="relative z-10 flex items-center justify-center gap-3">
                  <div className="text-center">
                    <p className="text-white text-lg font-bold mb-1">
                      Yeni Sinastri Hesaplayın
                    </p>
                    <p className="text-white/80 text-sm">
                      Yakınınızla uyumunuzu keşfedin
                    </p>
                  </div>
                </div>
                <div className="absolute inset-0 bg-gradient-to-r from-white/0 via-white/10 to-white/0 transform -skew-x-12 -translate-x-full group-hover:translate-x-full transition-transform duration-700"></div>
              </button>

              {/* Liste */}
              <div className="space-y-4">
                {isLoading ? (
                  <div className="text-center py-12">
                    <div className="inline-flex items-center space-x-2 text-gray-500">
                      <div className="w-5 h-5 border-2 border-[#3DA7B5] border-t-transparent rounded-full animate-spin"></div>
                      <span className="text-sm">Yükleniyor...</span>
                    </div>
                  </div>
                ) : filteredList && filteredList.length > 0 ? (
                  <div className="space-y-4">
                    {filteredList.map((item, index) => {
                      const leftUser = item?.users?.user?.name || "Kullanıcı";
                      const rightUser = item?.users?.partner?.name || "Partner";
                      const leftPic =
                        item?.users?.user?.profile_picture ||
                        "/assets/pngImages/user.png";
                      const rightPic =
                        item?.users?.partner?.profile_picture ||
                        "/assets/pngImages/user.png";
                      const requestStatus = item?.request_status; // "pending", "accepted", "rejected"
                      const isPending = requestStatus === "pending";
                      const isSender = item?.is_sender === true; // Gönderen kişi mi?

                      return (
                        <div
                          key={item.uuid || index}
                          className={`group relative overflow-hidden rounded-2xl p-6 transform transition-all duration-300 ${
                            isPending 
                              ? 'bg-gradient-to-br from-gray-50 to-gray-100 border border-gray-200' 
                              : 'bg-gradient-to-br from-[#3DA7B5]/10 via-[#5ABCC8]/5 to-white border border-[#3DA7B5]/20 cursor-pointer hover:scale-[1.02] hover:shadow-xl hover:border-[#3DA7B5]/40 active:scale-[0.98]'
                          }`}
                          onClick={() => {
                            // Sadece pending değilse analizi aç
                            if (!isPending) {
                              handleOpenAnalysis(item.uuid);
                            }
                          }}
                          style={{
                            animationDelay: `${index * 100}ms`,
                            animation: 'fadeInUp 0.6s ease-out forwards'
                          }}
                        >
                          {/* Gradient overlay effect */}
                          {!isPending && (
                            <div className="absolute inset-0 bg-gradient-to-br from-[#3DA7B5]/0 via-[#3DA7B5]/0 to-[#5ABCC8]/10 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                          )}
                          
                          <div className="relative z-10 flex items-center justify-between">
                            <div className="flex-1 min-w-0">
                              {/* İsimler ve ikon */}
                              <div className="flex items-center gap-3">
                                <div className="flex-shrink-0 w-10 h-10 rounded-xl bg-gradient-to-br from-[#3DA7B5] to-[#5ABCC8] flex items-center justify-center shadow-lg">
                                  <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
                                  </svg>
                                </div>
                                <div className="flex-1 min-w-0">
                                  <div className="space-y-1">
                                    <div className="text-sm font-bold text-gray-800 leading-tight line-clamp-1">
                                      {leftUser}
                                    </div>
                                    <div className="flex items-center gap-1.5">
                                      <div className="h-px flex-1 bg-gradient-to-r from-transparent via-gray-300 to-transparent"></div>
                                      <span className="text-[10px] text-gray-400 font-medium">ve</span>
                                      <div className="h-px flex-1 bg-gradient-to-r from-transparent via-gray-300 to-transparent"></div>
                                    </div>
                                    <div className="text-sm font-bold text-gray-800 leading-tight line-clamp-1">
                                      {rightUser}
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                            
                            {/* Pending durumları için farklı gösterimler */}
                            {isPending && !isSender ? (
                              // Gelen istek (is_sender: false): Kabul/Reddet butonları
                              <div className="flex items-center space-x-2 flex-shrink-0 ml-3" onClick={(e) => e.stopPropagation()}>
                                <button
                                  onClick={() => handleUpdateRequest(item.uuid, 1)} // 1 = kabul et
                                  disabled={updatingRequest === item.uuid}
                                  className="p-2.5 bg-gradient-to-br from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white rounded-xl transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed shadow-md hover:shadow-lg transform hover:scale-105"
                                  title="Kabul Et"
                                >
                                  <FaCheck className="w-4 h-4" />
                                </button>
                                <button
                                  onClick={() => handleUpdateRequest(item.uuid, 2)} // 2 = reddet
                                  disabled={updatingRequest === item.uuid}
                                  className="p-2.5 bg-gradient-to-br from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 text-white rounded-xl transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed shadow-md hover:shadow-lg transform hover:scale-105"
                                  title="Reddet"
                                >
                                  <FaTimes className="w-4 h-4" />
                                </button>
                              </div>
                            ) : isPending && isSender ? (
                              // Gönderilen istek (is_sender: true): Bekleniyor yazısı
                              <div className="flex items-center flex-shrink-0 ml-3">
                                <span className="text-xs text-amber-700 font-semibold bg-gradient-to-r from-amber-100 to-amber-50 px-4 py-2 rounded-xl border border-amber-200 shadow-sm">
                                  Bekleniyor
                                </span>
                              </div>
                            ) : (
                              // Kabul edilmiş: Ok ikonu ve durum
                              <div className="flex items-center gap-3 flex-shrink-0 ml-3">
                                <div className="flex items-center gap-2 px-3 py-1.5 bg-green-50 rounded-lg border border-green-200">
                                  <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                                  <span className="text-xs font-medium text-green-700">Aktif</span>
                                </div>
                                <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#3DA7B5] to-[#5ABCC8] flex items-center justify-center shadow-md group-hover:shadow-lg transition-all">
                                  <svg className="w-5 h-5 text-white transform group-hover:translate-x-0.5 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M9 5l7 7-7 7" />
                                  </svg>
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <div className="w-20 h-20 bg-gradient-to-br from-blue-50 to-indigo-50 rounded-full flex items-center justify-center mx-auto mb-4">
                      <svg className="w-10 h-10 text-[#3DA7B5]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                      </svg>
                    </div>
                    <h3 className="text-gray-600 font-medium mb-2">Henüz sinastri analizi yok</h3>
                    <p className="text-gray-500 text-sm">İlk sinastri analizinizi yapmak için yukarıdaki butona tıklayın</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Yeni Sinastri Popup */}
        {showNewSynastryPopup && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" onClick={() => !isSending && setShowNewSynastryPopup(false)}>
            <div className="bg-white rounded-2xl p-6 w-full max-w-sm mx-4" onClick={(e) => e.stopPropagation()}>
              <h3 className="text-lg font-semibold mb-4 text-gray-800">Yeni Sinastri İsteği</h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Jodiac Kullanıcı E-posta
                  </label>
                  <input
                    type="email"
                    value={partnerEmail}
                    onChange={(e) => setPartnerEmail(e.target.value)}
                    placeholder="kullanici@gmail.com"
                    className="w-full px-4 py-3 bg-white border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-[#3DA7B5] focus:border-transparent text-gray-800"
                  />
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => {
                      setShowNewSynastryPopup(false);
                      setPartnerEmail("");
                    }}
                    disabled={isSending}
                    className="flex-1 py-2 px-4 border border-gray-300 rounded-lg text-sm font-medium hover:bg-gray-50 transition-colors disabled:opacity-50"
                  >
                    İptal
                  </button>
                  <button
                    onClick={handleSendSynastryRequest}
                    disabled={isSending || !partnerEmail.trim()}
                    className="flex-1 py-2 px-4 bg-gradient-to-r from-[#3DA7B5] to-[#5ABCC8] text-white rounded-lg text-sm font-medium hover:from-[#2F8894] hover:to-[#4A9BA6] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isSending ? "Gönderiliyor..." : "Gönder"}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        <style jsx>{`
          @keyframes fadeInUp {
            from {
              opacity: 0;
              transform: translateY(20px);
            }
            to {
              opacity: 1;
              transform: translateY(0);
            }
          }
        `}</style>
      </div>
    );
  };

  // Analiz sonuçları ekranı
  const renderAnalysisStage = () => {
    const userData = packageData?.user
      ? {
          fullName: packageData.user.first_name + " " + packageData.user.last_name,
          profileImage: packageData.user.pictureDetails?.picture || "/assets/pngImages/user.png",
        }
      : {
          fullName: "Kullanıcı",
          profileImage: "/assets/pngImages/user.png",
        };

    const partnerData = synastryResult?.users?.partner
      ? {
          name: synastryResult.users.partner.name,
          profileImage:
            synastryResult.users.partner.profile_picture ||
            "/assets/pngImages/user.png",
          birthDate: synastryResult.users.partner.birth_date || "",
        }
      : {
          name: "",
          profileImage: "",
          birthDate: "",
        };

    return (
      <div className="w-full h-screen min-h-screen flex items-stretch justify-stretch sm:items-center sm:justify-center bg-[#e8e9ed]">
        <div className="flex flex-col items-center w-full min-h-full bg-white shadow-xl border border-gray-200 rounded-none sm:max-w-[390px] sm:h-[700px] sm:rounded-3xl relative overflow-y-auto">
          <div className="flex flex-col w-full h-full">
            {/* ModuleHeader */}
            <div className="relative z-30 w-full">
              <div className="relative">
                <div 
                  className="h-24 w-full"
                  style={{
                    backgroundImage: packageData?.package?.image_url ? `url(${packageData.package.image_url})` : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                  }}
                >
                  <div className="absolute inset-0 bg-gradient-to-b from-black/30 to-black/10"></div>
                </div>
                <button 
                  type="button" 
                  onClick={() => setCurrentStage(0)}
                  className="absolute top-4 left-4 z-10 w-8 h-8 bg-white/90 backdrop-blur-sm rounded-full flex items-center justify-center shadow-lg hover:bg-white transition-colors"
                >
                  <IoIosArrowBack className="text-xl text-gray-700" />
                </button>
              </div>
            </div>

            {/* Başlık */}
            <div className="px-6 pt-4 pb-2">
              <div className="text-center">
                <h2 className="text-xl font-bold text-gray-800 mb-1">Sinastri Analizi</h2>
                <p className="text-sm text-gray-500">İlişki Uyum Raporu</p>
              </div>
            </div>

            {/* Profile Comparison */}
            <div className="px-6 py-4 w-full">
              <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-2xl p-6 border border-blue-100">
                <div className="flex items-center justify-center gap-4">
                  {/* Sol Profil */}
                  <div className="flex flex-col items-center flex-1">
                    <div className="w-12 h-12 rounded-full shadow-md bg-gray-200 flex items-center justify-center">
                      <span className="text-lg font-bold text-gray-600">
                        {(() => {
                          const name = synastryResult?.users?.user?.name || userData.fullName;
                          const parts = name.trim().split(' ');
                          if (parts.length >= 2) {
                            return parts[0].charAt(0).toUpperCase() + parts[parts.length - 1].charAt(0).toUpperCase();
                          }
                          return name.charAt(0).toUpperCase();
                        })()}
                      </span>
                    </div>
                    <h3 className="text-base font-semibold text-gray-800 mt-2 text-center line-clamp-2">
                      {synastryResult?.users?.user?.name || userData.fullName}
                    </h3>
                  </div>

                  {/* Ortadaki Yıldız İkonu */}
                  <div className="flex-shrink-0 relative">
                    <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[#3DA7B5] to-[#5ABCC8] flex items-center justify-center shadow-lg">
                      <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
                      </svg>
                    </div>
                    <div className="absolute inset-0 rounded-full bg-[#3DA7B5] animate-ping opacity-20"></div>
                  </div>

                  {/* Sağ Profil */}
                  <div className="flex flex-col items-center flex-1">
                    <div className="w-12 h-12 rounded-full  shadow-md bg-gray-200 flex items-center justify-center">
                      <span className="text-lg font-bold text-gray-600">
                        {(() => {
                          const name = partnerData.name || "Partner";
                          const parts = name.trim().split(' ');
                          if (parts.length >= 2) {
                            return parts[0].charAt(0).toUpperCase() + parts[parts.length - 1].charAt(0).toUpperCase();
                          }
                          return name.charAt(0).toUpperCase();
                        })()}
                      </span>
                    </div>
                    <h3 className="text-base font-semibold text-gray-800 mt-2 text-center line-clamp-2">
                      {partnerData.name || "Partner"}
                    </h3>
                  </div>
                </div>
              </div>
            </div>

            {/* Dinamik Analiz Kartları */}
            <div className="flex-1 px-6 pb-28 space-y-4">
              {buildAnalysisCards(synastryResult).map((analysis) => {
                const getIcon = (iconType, color) => {
                  const iconProps = { className: "w-5 h-5", style: { color } };
                  
                  switch (iconType) {
                    case "heart":
                      return (
                        <svg {...iconProps} fill="currentColor" viewBox="0 0 24 24">
                          <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z"/>
                        </svg>
                      );
                    case "chat":
                      return (
                        <svg {...iconProps} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                        </svg>
                      );
                    case "fire":
                      return (
                        <svg {...iconProps} fill="currentColor" viewBox="0 0 24 24">
                          <path d="M12 2c1.5 3.5 5 5 5 9 0 2.8-2.2 5-5 5s-5-2.2-5-5c0-1.7.7-3.2 1.8-4.3C8 5.3 8 3.8 8 2c0 0 0 0 0 0 0 0 1-2 4-2zm0 20c-3.9 0-7-3.1-7-7 0-2.4 1.2-4.6 3.2-6C7.5 10.8 7 12.3 7 14c0 2.8 2.2 5 5 5s5-2.2 5-5c0-1.7-.5-3.2-1.2-4.5C17.8 10.4 19 12.6 19 15c0 3.9-3.1 7-7 7z"/>
                        </svg>
                      );
                    case "users":
                      return (
                        <svg {...iconProps} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                        </svg>
                      );
                    case "lightning":
                      return (
                        <svg {...iconProps} fill="currentColor" viewBox="0 0 24 24">
                          <path d="M13 2L3 14h8l-1 8 10-12h-8l1-8z"/>
                        </svg>
                      );
                    default:
                      return null;
                  }
                };

                return (
                  <div
                    key={analysis.id}
                    className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100 hover:shadow-md transition-shadow"
                  >
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center gap-3">
                        <div 
                          className="w-10 h-10 rounded-xl flex items-center justify-center shadow-sm"
                          style={{ backgroundColor: analysis.bgColor }}
                        >
                          {getIcon(analysis.iconType, analysis.color)}
                        </div>
                        <span className="font-bold text-base text-gray-800">
                          {analysis.title}
                        </span>
                      </div>
                      <div className="flex flex-col items-end">
                        <span className="text-2xl font-bold" style={{ color: analysis.color }}>
                          {analysis.percentage}%
                        </span>
                      </div>
                    </div>
                    
                    <div className="w-full bg-gray-100 rounded-full h-2.5 mb-4 overflow-hidden">
                      <div
                        className="h-2.5 rounded-full transition-all duration-700 ease-out"
                        style={{ 
                          width: `${analysis.percentage}%`,
                          background: `linear-gradient(90deg, ${analysis.color}dd, ${analysis.color})`
                        }}
                      />
                    </div>
                    
                    <div className="space-y-2">
                      {analysis.subtitle && (
                        <h4 className="font-semibold text-xs text-gray-700 uppercase tracking-wide">
                          {analysis.subtitle}
                        </h4>
                      )}
                      {analysis.description && (
                        <p className="text-sm text-gray-600 leading-relaxed">
                          {analysis.description}
                        </p>
                      )}
                    </div>
                  </div>
                );
              })}

              {/* Öneriler Bölümü */}
              {synastryResult?.summary?.recommendations && synastryResult.summary.recommendations.length > 0 && (
                <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-2xl p-6 shadow-sm border border-blue-200">
                  <div className="flex items-center gap-3 mb-5">
                    <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl flex items-center justify-center shadow-md">
                      <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                      </svg>
                    </div>
                    <h3 className="text-lg font-bold text-gray-800">Öneriler</h3>
                  </div>
                  <div className="space-y-3">
                    {synastryResult.summary.recommendations.map((recommendation, index) => (
                      <div key={index} className="flex gap-3 items-start">
                        <div className="w-6 h-6 rounded-full bg-blue-200 flex items-center justify-center flex-shrink-0 mt-0.5">
                          <span className="text-xs font-bold text-blue-700">{index + 1}</span>
                        </div>
                        <p className="text-sm text-gray-700 leading-relaxed flex-1">
                          {recommendation}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Güçlü Yönler Bölümü */}
              {synastryResult?.summary?.strengths && synastryResult.summary.strengths.length > 0 && (
                <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-2xl p-6 shadow-sm border border-green-200">
                  <div className="flex items-center gap-3 mb-5">
                    <div className="w-10 h-10 bg-gradient-to-br from-green-500 to-emerald-600 rounded-xl flex items-center justify-center shadow-md">
                      <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
                      </svg>
                    </div>
                    <h3 className="text-lg font-bold text-gray-800">Güçlü Yönler</h3>
                  </div>
                  <div className="space-y-3">
                    {synastryResult.summary.strengths.map((strength, index) => (
                      <div key={index} className="flex gap-3 items-start">
                        <div className="w-6 h-6 rounded-full bg-green-200 flex items-center justify-center flex-shrink-0 mt-0.5">
                          <svg className="w-3.5 h-3.5 text-green-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                          </svg>
                        </div>
                        <p className="text-sm text-gray-700 leading-relaxed flex-1">
                          {strength}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Ana return
  if (currentStage === 0) return renderListStage();
  return renderAnalysisStage();
}

export default Synastry;

